import { useQuery } from '@tanstack/react-query';
import {
  CrmCustomFieldsOptionsService,
  CrmCustomFieldsDefinitionsOptionsService,
  CustomFieldListOption,
  CustomFieldListOptionGetResponse,
  CustomFieldListOptionListQueryParams,
  CustomFieldListOptionListResponse,
  CustomFieldListOptionCreateBodyParams,
  CustomFieldListOptionUpdateBodyParams,
} from 'src/api/generated';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UseCustomFieldListOptionsParams
  extends CustomFieldListOptionListQueryParams {
  enabled?: boolean;
}

interface UseCustomFieldListOptionsResponse extends ApiResponse {
  options: CustomFieldListOption[];
}

/**
 * Hook for fetching list options
 */
export const useCustomFieldListOptions = (
  params: UseCustomFieldListOptionsParams
): UseCustomFieldListOptionsResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchOptions = async (): Promise<CustomFieldListOptionListResponse> => {
    return await CrmCustomFieldsOptionsService.getCustomFieldListOptions(
      queryParams.filterBy,
      queryParams.searchBy,
      queryParams.sortBy,
      queryParams.page,
      queryParams.pageSize,
      queryParams.pageToken
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/custom-fields/list-options', queryParams],
    queryFn: fetchOptions,
    enabled: enabled,
  });

  const options = useMemoizedArrayData<CustomFieldListOption>(
    data?.items || []
  );

  return {
    loading,
    error,
    options,
    refetch,
  };
};

interface UseCustomFieldListOptionResponse extends ApiResponse {
  option: CustomFieldListOption | null;
}

/**
 * Hook for fetching a single list option by ID
 */
export const useCustomFieldListOption = (
  definitionId: number,
  optionId: number,
  enabled: boolean = true
): UseCustomFieldListOptionResponse => {
  const fetchOption = async (): Promise<CustomFieldListOptionGetResponse> => {
    return await CrmCustomFieldsDefinitionsOptionsService.getCustomFieldListOption(
      definitionId,
      optionId
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [
      'crm/custom-fields/definitions/list-options',
      definitionId,
      optionId,
    ],
    queryFn: fetchOption,
    enabled: enabled && !!definitionId && !!optionId,
  });

  return {
    loading,
    error,
    option: data || null,
    refetch,
  };
};

// Mutation hooks for list options CRUD operations

interface UseCreateCustomFieldListOptionParams {
  onSuccess?: (option: CustomFieldListOption) => void;
  onError?: (error: Error) => void;
}

interface CreateCustomFieldListOptionData
  extends CustomFieldListOptionCreateBodyParams {}

interface UseCreateCustomFieldListOptionResponse {
  createOption: (
    data: CreateCustomFieldListOptionData
  ) => Promise<CustomFieldListOption>;
  loading: boolean;
  error: Error | null;
}

export const useCreateCustomFieldListOption = (
  params: UseCreateCustomFieldListOptionParams = {}
): UseCreateCustomFieldListOptionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const createOptionMutation = async (
    data: CreateCustomFieldListOptionData
  ): Promise<CustomFieldListOption> => {
    return await CrmCustomFieldsDefinitionsOptionsService.createCustomFieldListOption(
      data.definitionId,
      data
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CustomFieldListOption,
    Error,
    CreateCustomFieldListOptionData
  >({
    mutationFn: createOptionMutation,
    onSuccess: (option) => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/list-options'],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions/list-options'],
      });
      onSuccess?.(option);
    },
    onError,
  });

  return {
    createOption: mutateAsync,
    loading,
    error: error || null,
  };
};

interface UseUpdateCustomFieldListOptionParams {
  onSuccess?: (option: CustomFieldListOption) => void;
  onError?: (error: Error) => void;
}

interface UpdateCustomFieldListOptionData {
  definitionId: number;
  optionId: number;
  data: Pick<
    CustomFieldListOptionUpdateBodyParams,
    'value' | 'label' | 'description'
  >;
}

interface UseUpdateCustomFieldListOptionResponse {
  updateOption: (
    data: UpdateCustomFieldListOptionData
  ) => Promise<CustomFieldListOption>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateCustomFieldListOption = (
  params: UseUpdateCustomFieldListOptionParams = {}
): UseUpdateCustomFieldListOptionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const updateOptionMutation = async (
    data: UpdateCustomFieldListOptionData
  ): Promise<CustomFieldListOption> => {
    const { definitionId, optionId, data: updateData } = data;

    return await CrmCustomFieldsDefinitionsOptionsService.updateCustomFieldListOption(
      definitionId,
      optionId,
      updateData
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CustomFieldListOption,
    Error,
    UpdateCustomFieldListOptionData
  >({
    mutationFn: updateOptionMutation,
    onSuccess: (option) => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/list-options'],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions/list-options'],
      });
      onSuccess?.(option);
    },
    onError,
  });

  return {
    updateOption: mutateAsync,
    loading,
    error: error || null,
  };
};

interface UseDeleteCustomFieldListOptionParams {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface DeleteCustomFieldListOptionData {
  definitionId: number;
  optionId: number;
}

interface UseDeleteCustomFieldListOptionResponse {
  deleteOption: (data: DeleteCustomFieldListOptionData) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteCustomFieldListOption = (
  params: UseDeleteCustomFieldListOptionParams = {}
): UseDeleteCustomFieldListOptionResponse => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const deleteOptionMutation = async (
    data: DeleteCustomFieldListOptionData
  ): Promise<void> => {
    const { definitionId, optionId } = data;
    await CrmCustomFieldsDefinitionsOptionsService.deleteCustomFieldListOption(
      definitionId,
      optionId
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<void, Error, DeleteCustomFieldListOptionData>({
    mutationFn: deleteOptionMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/list-options'],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions/list-options'],
      });
      onSuccess?.();
    },
    onError,
  });

  return {
    deleteOption: mutateAsync,
    loading,
    error: error || null,
  };
};
